import type { IncidentDomainResponse } from '@prerender/diagnostics-api';
import { type KeyboardEvent, useRef } from 'react';

import { DOMAIN_TABS, type DomainTab, domainsInTab, domainTabLabel } from './domainTabFilter';

type DomainTabsProps = {
  domains: readonly IncidentDomainResponse[];
  selected: DomainTab;
  onSelect: (tab: DomainTab) => void;
  idPrefix: string;
  panelId: string;
};

const STEP_BY_KEY: ReadonlyMap<string, number> = new Map([
  ['ArrowRight', 1],
  ['ArrowLeft', -1],
]);

export function domainTabId(idPrefix: string, tab: DomainTab): string {
  return `${idPrefix}-${tab}`;
}

function DomainTabs({ domains, selected, onSelect, idPrefix, panelId }: DomainTabsProps) {
  const buttons = useRef(new Map<DomainTab, HTMLButtonElement>());

  const moveSelection = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = STEP_BY_KEY.get(event.key);
    if (step === undefined) return;

    event.preventDefault();
    const next = DOMAIN_TABS[(DOMAIN_TABS.indexOf(selected) + step + DOMAIN_TABS.length) % DOMAIN_TABS.length];
    onSelect(next);
    buttons.current.get(next)?.focus();
  };

  return (
    <div role="tablist" aria-label="Domain states" className="diagnostics-domain-tabs" onKeyDown={moveSelection}>
      {DOMAIN_TABS.map((tab) => (
        <button
          key={tab}
          ref={(button) => {
            if (button !== null) buttons.current.set(tab, button);
          }}
          id={domainTabId(idPrefix, tab)}
          type="button"
          role="tab"
          className="diagnostics-domain-tab"
          aria-selected={tab === selected}
          aria-controls={panelId}
          tabIndex={tab === selected ? 0 : -1}
          onClick={() => onSelect(tab)}
        >
          {`${domainTabLabel(tab)} (${domainsInTab(domains, tab).length})`}
        </button>
      ))}
    </div>
  );
}

export default DomainTabs;
